import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

const CreateTicketModal = ({ 
  show, 
  onClose, 
  ticket = null, // null for new, object for edit
  onSave, 
  submitting = false 
}) => {
  const { t } = useTranslation();
  const isEdit = !!ticket;
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    priority: "MEDIUM",
    category: ""
  }); 

  // Populate form data for edit mode
  useEffect(() => {
    if (show) {
      if (isEdit && ticket) {
        setFormData({
          subject: ticket.subject || "",
          description: ticket.description || "",
          priority: ticket.priority || "MEDIUM",
          category: ticket.category || ""
        });
      } else {
        // Reset form for new ticket
        setFormData({
          subject: "",
          description: "",
          priority: "MEDIUM",
          category: ""
        });
      }
    }
  }, [show, isEdit, ticket]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (submitting) return;
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Error in modal submit:", error);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: "",
      description: "",
      priority: "MEDIUM", 
      category: ""
    });
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? t("Edit Support Ticket") : t("Create New Support Ticket")}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Subject *</Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter ticket subject"
                  required
                />
              </Form.Group>
            </div>
            
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Category *</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="PAYROLL">Payroll</option>
                  <option value="ATTENDANCE">Attendance</option>
                  <option value="BUG">Bug Report</option>
                  <option value="LEAVE">Leave</option>
                  <option value="FEATURE_REQUEST">Feature Request</option>
                  <option value="OTHER">Other</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the issue in detail..."
              required
            />
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa fa-spinner fa-spin me-1" />
                {isEdit ? t("Updating...") : t("Creating...")}
              </>
            ) : (
              isEdit ? t("Update Ticket") : t("Create Ticket")
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;